import { Popover, Transition } from '@headlessui/react';
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from "prop-types";

const PANEL_MARGIN = 8;
const DEFAULT_Z_INDEX = 1000001;

const getScrollableParents = (node) => {
  const parents = [];
  let current = node?.parentElement;

  while (current) {
    const { overflow, overflowY, overflowX } = window.getComputedStyle(current);
    const isScrollable = /(auto|scroll|overlay)/.test(`${overflow}${overflowY}${overflowX}`);
    if (isScrollable) {
      parents.push(current);
    }
    current = current.parentElement;
  }

  return parents;
};

const computePanelPosition = ({ triggerRect, panelRect, placement, alignRight }) => {
  const panelHeight = panelRect.height;
  const panelWidth = panelRect.width;
  const spaceBelow = window.innerHeight - triggerRect.bottom - PANEL_MARGIN;
  const spaceAbove = triggerRect.top - PANEL_MARGIN;

  let actualPlacement = placement;
  if (placement === 'auto') {
    if (spaceBelow >= panelHeight) {
      actualPlacement = 'bottom';
    } else if (spaceAbove >= panelHeight) {
      actualPlacement = 'top';
    } else {
      actualPlacement = spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }
  }

  let top = actualPlacement === 'top'
    ? triggerRect.top - panelHeight - PANEL_MARGIN
    : triggerRect.bottom + PANEL_MARGIN;

  let left = alignRight
    ? triggerRect.right - panelWidth
    : triggerRect.left;

  const minLeft = PANEL_MARGIN;
  const maxLeft = window.innerWidth - panelWidth - PANEL_MARGIN;
  left = Math.max(minLeft, Math.min(left, maxLeft));

  const minTop = PANEL_MARGIN;
  const maxTop = window.innerHeight - panelHeight - PANEL_MARGIN;

  if (actualPlacement === 'top') {
    // Never let a top-placed panel go above the viewport.
    top = Math.max(top, minTop);
  } else {
    // Never let a bottom-placed panel go below the viewport,
    // but don't pull it up past its natural position under the trigger
    // (that would make it look like it flipped sides).
    top = Math.min(top, maxTop);
  }

  return { top, left, actualPlacement };
};

const PortaledPanel = ({
  open,
  close,
  triggerRef,
  placement,
  alignRight,
  zIndex,
  className,
  hasCustomWidth,
  content,
}) => {
  const panelRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, actualPlacement: 'bottom' });
  const [isPositioned, setIsPositioned] = useState(false);
  // Locks in the resolved placement ('top' | 'bottom') the first time it's
  // measured after opening, so subsequent scroll/resize updates reuse the
  // same side instead of re-evaluating 'auto' and flipping mid-scroll.
  const resolvedPlacementRef = useRef(null);
  const initialTopRef = useRef(null);

  const updatePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const panelEl = panelRef.current;
    if (!open || !triggerEl || !panelEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const panelRect = panelEl.getBoundingClientRect();
    if (!panelRect.height || !panelRect.width) return;

    if (resolvedPlacementRef.current === null && panelRef.current) {
      try {
        panelRef.current.style.minHeight = `${panelRect.height}px`;
      } catch (e) {
        // ignore
      }
    }

    const effectivePlacement = resolvedPlacementRef.current ?? placement;

    const result = computePanelPosition({triggerRect, panelRect, placement: effectivePlacement, alignRight});

    if (resolvedPlacementRef.current === null) {
      resolvedPlacementRef.current = result.actualPlacement;
      // Lock the initial top for later clamping when opened on top.
      initialTopRef.current = result.top;
    } else {
      if (result.actualPlacement === 'top' && initialTopRef.current != null) {
        result.top = Math.max(result.top, initialTopRef.current);
      }
    }

    setPosition(result);
    setIsPositioned(true);
  }, [open, placement, alignRight, triggerRef]);

  useLayoutEffect(() => {
    if (!open) {
      setIsPositioned(false);
      // Reset so the next time this popover opens, placement is freely
      // re-evaluated based on the viewport space available at that moment.
      resolvedPlacementRef.current = null;
      initialTopRef.current = null;
      try {
        if (panelRef.current) panelRef.current.style.minHeight = '';
      } catch (e) {
        // ignore
      }
      return undefined;
    }

    updatePosition();
    const frameId = window.requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const scrollParents = getScrollableParents(triggerRef.current);
    scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', updatePosition, { passive: true });
    });

    let resizeObserver;
    const resizeFrameId = window.requestAnimationFrame(() => {
      if (typeof ResizeObserver !== 'undefined' && panelRef.current) {
        resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(panelRef.current);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(resizeFrameId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      scrollParents.forEach((parent) => {
        parent.removeEventListener('scroll', updatePosition);
      });
      resizeObserver?.disconnect();
    };
  }, [open, updatePosition, triggerRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Transition
      as={Fragment}
      show={open}
      enter="transition ease-out duration-200"
      enterFrom="opacity-0 translate-y-1"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-1"
    >
      <Popover.Panel
        static
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex,
          visibility: isPositioned ? 'visible' : 'hidden',
          boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.15)',
        }}
        className={`bg-white rounded-[8px] ${!hasCustomWidth ? 'w-max' : ''} ${className}`}
      >
        <div ref={panelRef}>
          {content({ close })}
        </div>
      </Popover.Panel>
    </Transition>,
    document.body
  );
};

export const Popup = ({
  component,
  content,
  className,
  alignRight,
  widthClass,
  portalToBody = false,
  placement = 'auto',
  zIndex = DEFAULT_Z_INDEX,
}) => {
  const componentRef = useRef();
  const hasCustomWidth = className?.includes('w-[');

  return (
    <Popover className="relative flex items-center">
      {({ close, open }) => (
        <>
          <Popover.Button className={`${widthClass}`} ref={componentRef}>
            {() => component({ componentRef, open })}
          </Popover.Button>
          {portalToBody ? (
            <PortaledPanel
              open={open}
              close={close}
              triggerRef={componentRef}
              placement={placement}
              alignRight={alignRight}
              zIndex={zIndex}
              className={className}
              hasCustomWidth={hasCustomWidth}
              content={content}
            />
          ) : (
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                static
                style={{ boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.15)' }}
                className={`absolute z-20 bg-white rounded-[8px] top-full mt-2 ${!hasCustomWidth ? 'w-max' : ''} ${className} ${alignRight && 'right-0'}`}
              >
                {(props) => content({ ...props, close })}
              </Popover.Panel>
            </Transition>
          )}
        </>
      )}
    </Popover>
  );
};

Popup.propTypes = {
  component: PropTypes.func,
  content: PropTypes.func,
  className: PropTypes.string,
  alignRight: PropTypes.bool,
  widthClass: PropTypes.string,
  portalToBody: PropTypes.bool,
  placement: PropTypes.oneOf(['auto', 'top', 'bottom']),
  zIndex: PropTypes.number,
};

Popup.defaultProps = {
  className: 'p-[20px]',
  alignRight: false,
  widthClass: '',
  portalToBody: false,
  placement: 'auto',
  zIndex: DEFAULT_Z_INDEX,
};
